import { Modal } from '@fabric-ds/react';
import { toast } from '@fabric-ds/elements';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { trackEventSignupInterestSend, trackPhoneNumberUserInterest } from 'common/lib/helpers/trackingService';
import { useDataContext } from 'common/lib/helpers/DataContext';
import { isUnitAd } from 'common/lib/helpers/adTypeHelpers';
import * as S from './styles';
import { post, getWithHeaders } from '../../helpers/Rest';

const isBlank = val => {
    if (val) {
        return val.trim() === '';
    }
    return true;
};

const InterestModal = ({ showModal, closeModal }) => {
    const { projectName, adType, requireLeadPhoneNumber, finnkode, interestUrl, profileUrl, userId, master } =
        useDataContext();
    const tlfIsRequired = requireLeadPhoneNumber === true;

    const initalErrors = { name: '', email: '' };
    if (tlfIsRequired) initalErrors.tlf = '';

    const [nameField, setNameField] = useState('');
    const [emailField, setEmailField] = useState('');
    const [tlfField, setTlfField] = useState('');
    const [errors, setErrors] = useState(initalErrors);
    const [showTlf, setShowTlf] = useState(tlfIsRequired);
    const [loading, setLoading] = useState(false);
    const tlfRef = useRef();
    const ctaRef = useRef();

    const setErrorMessage = (id, errorMsg) => {
        errors[id] = errorMsg;
        setErrors(errors);
    };

    const validate = (input, invalidInputErrorMessage) => {
        const missingValue = input.required && !input.value;
        const regexFailed = input.pattern && input.value && !new RegExp(input.pattern).test(input.value);
        const doubleDotInEmail = input.type === 'email' && input.value.includes('..');

        if (missingValue) {
            setErrorMessage(input.id, `Du må fylle ut ${input.name.toLowerCase()}`);
        } else if (regexFailed || doubleDotInEmail) {
            const errorMessage = invalidInputErrorMessage || `Ugyldig ${input.name.toLowerCase()}`;
            setErrorMessage(input.id, errorMessage);
        } else {
            delete errors[input.id];
        }
    };

    const handleNameChange = event => {
        setNameField(event.target.value);
        validate(event.target, 'Navn må bestå av fornavn og etternavn');
    };
    const handleEmailChange = event => {
        setEmailField(event.target.value);
        validate(event.target);
    };
    const handleTlfChange = event => {
        setTlfField(event.target.value);
        validate(event.target);
    };
    const handleToggleTlf = () => {
        setShowTlf(!showTlf);
        if (!showTlf) {
            trackPhoneNumberUserInterest();
            setErrors(errors => ({ ...errors, tlf: '' }));
        }
        setTlfField('');
        delete errors.tlf;
    };

    const getProjectName = () => {
        if (isUnitAd(adType)) {
            return master ? master.projectName : '';
        }
        return projectName;
    };

    const handleSendInterest = () => {
        setLoading(true);
        const tlf = tlfField !== '' && showTlf ? tlfField : null;

        const bodyContent = {
            finnkode,
            name: nameField.trim(),
            interestedEmail: emailField.trim(),
            telephoneNumber: tlf,
        };

        const success = () => {
            trackEventSignupInterestSend();
            setLoading(false);
            closeModal();
            toast('Hurra! Du er registrert.', {
                type: 'success',
            });
        };

        const error = response => {
            setLoading(false);
            // eslint-disable-next-line no-console
            console.error('REST error', response);
            const { errorCode, userMessage } = response;
            const emailValidationError = getEmailValidationError(response);
            if (errorCode === 400 && emailValidationError) {
                setErrors({ ...errors, email: emailValidationError.error });
            } else if (errorCode === 409) {
                setErrors({ ...errors, email: userMessage });
            } else {
                toast('Noe gikk galt. Prøv igjen senere.', {
                    type: 'error',
                });
                closeModal();
            }
        };

        const getEmailValidationError = response => {
            const { validationErrors } = response;
            if (!_.isEmpty(validationErrors)) {
                return _.find(validationErrors, e => e.code === 'Email');
            }
            return null;
        };

        post(interestUrl, JSON.stringify(bodyContent), success, error);
    };

    const prefillFields = () => {
        const emptyFields = nameField === '' && emailField === '' && tlfField === '';

        if (userId && emptyFields) {
            getWithHeaders(
                profileUrl,
                { AuthLoginId: userId },
                res => {
                    setNameField(res.fullName);
                    !isBlank(res.fullName) && delete errors.name;
                    setEmailField(res.email);
                    !isBlank(res.email) && delete errors.email;
                    setTlfField(res.phoneNumber);
                    !isBlank(res.phoneNumber) && delete errors.tlf;
                },
                // eslint-disable-next-line no-console
                error => console.log('Fetch Error : ', error),
                true
            );
        }
    };

    const hasErrorInFields = () => {
        return Object.keys(errors).length !== 0;
    };

    useEffect(() => {
        if (showModal) prefillFields();
    }, [showModal]);

    const tlfCheckbox = (
        <>
            <p className="mt-16">Vil du også bli kontaktet på telefon?</p>
            <S.ModalCheckbox
                type="checkbox"
                label="Ja takk"
                defaultChecked={showTlf}
                onChange={() => handleToggleTlf()}
            />
        </>
    );

    return (
        <Modal
            open={showModal}
            onDismiss={() => closeModal()}
            right
            initialFocusRef={ctaRef}
            aria-label="Meld interesse"
            title={`Motta oppdateringer om ${_.truncate(getProjectName(), {
                length: 64,
            })}`}
            footer={
                <button
                    type="button"
                    className={`button button--primary max-w-full w-1/3 ${loading ? 'button--in-progress' : ''}`}
                    onClick={() => handleSendInterest()}
                    disabled={hasErrorInFields()}
                >
                    Send inn
                </button>
            }
        >
            <p>
                FINN.no videreformidler din kontaktinformasjon til annonsøren slik at du kan bli holdt løpende oppdatert
                om prosjektet.
            </p>

            <div
                className={`input input--text input--kill-bottom-margin mt-16 ${errors.name && 'input--is-invalid'}`}
                data-identifier="name"
            >
                <label htmlFor="name">
                    Navn
                    <input
                        type="text"
                        id="name"
                        name="navn"
                        pattern="[^\s\\]+\s[^\s\\]+"
                        required
                        value={nameField || ''}
                        onChange={e => handleNameChange(e)}
                        ref={ctaRef}
                        className="text-16"
                    />
                </label>

                <div className="input__sub-text">
                    <span className="input__sub-text__text-hint" />
                    <span className="input__sub-text__error">{errors.name}</span>
                </div>
            </div>
            <div
                className={`input input--text input--kill-bottom-margin mt-16 ${errors.email && 'input--is-invalid'}`}
                data-identifier="email"
            >
                <label htmlFor="email">
                    E-post
                    <input
                        type="email"
                        id="email"
                        pattern="^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$"
                        name="e-post"
                        required
                        value={emailField || ''}
                        onChange={e => handleEmailChange(e)}
                        className="text-16"
                    />
                </label>

                <div className="input__sub-text">
                    <span className="input__sub-text__text-hint" />
                    <span className="input__sub-text__error">{errors.email}</span>
                </div>
            </div>
            {!tlfIsRequired && tlfCheckbox}
            <div
                className={`input input--number input--kill-bottom-margin ${errors.tlf && 'input--is-invalid'}`}
                hidden={!showTlf}
                data-identifier="tlf"
            >
                <label htmlFor="tlf">
                    Telefonnummer
                    <input
                        type="tel"
                        id="tlf"
                        pattern="^(00\d{2}|\+\d{2})?\d{8}$"
                        name="telefonnummer"
                        value={tlfField || ''}
                        required={tlfIsRequired}
                        onChange={e => handleTlfChange(e)}
                        ref={tlfRef}
                        className="text-16"
                    />
                </label>

                <div className="input__sub-text">
                    <span className="input__sub-text__text-hint" />
                    <span className="input__sub-text__error">{errors.tlf}</span>
                </div>
            </div>

            <p className="text-gray-500 text-12 mt-16">
                Når du trykker “Send inn” ber du oss formidle innholdet i skjemaet til annonsøren(e) for at de skal
                kontakte deg gjennom den kontaktinformasjonen du har oppgitt. Annonsøren(e) blir selvstendig
                behandlingsansvarlige for personopplysningene de mottar. FINN.nos oversendelse av data skjer i henhold
                til GDPRs artikkel 6, 1 (b): “Oppfyllelse av avtale”.{' '}
                <a
                    href="https://hjelpesenter.finn.no/hc/no/articles/360006266280"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {' '}
                    Les mer
                </a>
            </p>
        </Modal>
    );
};

InterestModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    closeModal: PropTypes.func.isRequired,
};

export default InterestModal;
